import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useIntegrationConfigs } from "@/hooks/useIntegrationConfigs";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Shield, CheckCircle, XCircle, Save, Eye, EyeOff, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API_BASE_URL = "https://uat-api.billportal.io/api/v1";

export default function IntegrationSettings() {
  const { currentWorkspace } = useWorkspace();
  const { getIntegrationConfig, testConnection, syncSuppliers } = useIntegrationConfigs();
  const { data: parserBotConfig, isLoading: loadingToken } = getIntegrationConfig('parser_bot');
  
  const [bearerToken, setBearerToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [supplierCount, setSupplierCount] = useState<number>(0);

  useEffect(() => {
    if (parserBotConfig?.credentials && typeof parserBotConfig.credentials === 'object') {
      const creds = parserBotConfig.credentials as { bearer_token?: string };
      setBearerToken(creds.bearer_token || '');
    }
    if (parserBotConfig?.connection_status === 'connected' && parserBotConfig?.metadata) {
      const meta = parserBotConfig.metadata as { supplier_count?: number };
      setSupplierCount(meta.supplier_count || 0);
    }
  }, [parserBotConfig]);

  const handleSaveAndTest = async () => {
    if (!bearerToken) {
      toast.error('Please enter a Bearer token');
      return;
    }
    
    if (!currentWorkspace?.id) {
      toast.error('No workspace selected. Please select a workspace first.');
      return;
    }

    try {
      // Test connection with explicit workspaceId
      const result = await testConnection.mutateAsync({
        integrationType: 'parser_bot',
        apiBaseUrl: API_BASE_URL,
        bearerToken,
        workspaceId: currentWorkspace.id,
      });

      if (result.success) {
        setSupplierCount(result.supplierCount);
        
        // Automatically sync suppliers after successful connection
        await syncSuppliers.mutateAsync(currentWorkspace.id);
      }
    } catch (error) {
      // Error already handled by mutation
      console.error('[IntegrationSettings] Save & Test failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integration Settings"
        description="Configure external API integrations for automated bill parsing"
      />

      {/* Workspace Warning */}
      {!currentWorkspace?.id && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Workspace Selected</AlertTitle>
          <AlertDescription>
            Please select a workspace from the sidebar before configuring integrations.
          </AlertDescription>
        </Alert>
      )}

      {/* Admin-Only Notice */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>Admin Configuration</AlertTitle>
        <AlertDescription>
          These settings can only be modified by workspace administrators. 
          Once configured, all users in the workspace will automatically have access to these integrations.
        </AlertDescription>
      </Alert>

      {/* BillPortal.io Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>BillPortal.io Vendor API</CardTitle>
              <CardDescription>
                Configure authentication for automatic bill parsing via vendor-specific APIs
              </CardDescription>
            </div>
            {parserBotConfig?.connection_status === 'connected' && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
            {parserBotConfig?.connection_status === 'error' && (
              <Badge variant="destructive">
                <XCircle className="h-3 w-3 mr-1" />
                Connection Failed
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* API Base URL (Read-only) */}
            <div className="space-y-2">
              <Label>API Base URL</Label>
              <Input
                value={API_BASE_URL}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                This is the production endpoint for BillPortal.io parsing API
              </p>
            </div>

            <Separator />

            {/* Bearer Token Input */}
            <div className="space-y-2">
              <Label htmlFor="bearer_token">
                Bearer Token <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="bearer_token"
                    type={showToken ? "text" : "password"}
                    value={bearerToken}
                    onChange={(e) => setBearerToken(e.target.value)}
                    placeholder="Enter your Bearer token"
                    className="pr-10"
                    disabled={loadingToken}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <Button 
                  onClick={handleSaveAndTest}
                  disabled={!bearerToken || !currentWorkspace?.id || testConnection.isPending || syncSuppliers.isPending}
                >
                  {(testConnection.isPending || syncSuppliers.isPending) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {testConnection.isPending ? 'Testing...' : 'Syncing...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save & Test
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Token is stored securely and encrypted at rest
              </p>
            </div>

            {/* Connection Status */}
            {parserBotConfig?.connection_status === 'connected' && supplierCount > 0 && (
              <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertTitle>Connection Verified</AlertTitle>
                <AlertDescription>
                  Successfully connected to BillPortal.io API. 
                  Found <strong>{supplierCount} available suppliers</strong> for vendor parsing.
                </AlertDescription>
              </Alert>
            )}

            {parserBotConfig?.connection_status === 'error' && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Connection Failed</AlertTitle>
                <AlertDescription>
                  Unable to connect to BillPortal.io API. Please verify:
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li>Bearer token is correct and not expired</li>
                    <li>Your network allows access to {API_BASE_URL}</li>
                    <li>The API service is operational</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle>How Vendor API Integration Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <div className="font-bold text-blue-600 dark:text-blue-400 text-lg">1</div>
              <div>
                <h4 className="font-semibold mb-1">One-Time Setup</h4>
                <p className="text-sm text-muted-foreground">
                  Admin configures Bearer token once in settings (this page)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-950 rounded-lg">
              <div className="font-bold text-green-600 dark:text-green-400 text-lg">2</div>
              <div>
                <h4 className="font-semibold mb-1">Auto-Load Suppliers</h4>
                <p className="text-sm text-muted-foreground">
                  System automatically fetches available suppliers from BillPortal.io
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <div className="font-bold text-purple-600 dark:text-purple-400 text-lg">3</div>
              <div>
                <h4 className="font-semibold mb-1">Configure Vendors</h4>
                <p className="text-sm text-muted-foreground">
                  Go to Processing → Sources tab to select which vendors to enable for auto-parsing
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
              <div className="font-bold text-orange-600 dark:text-orange-400 text-lg">4</div>
              <div>
                <h4 className="font-semibold mb-1">Automatic Parsing</h4>
                <p className="text-sm text-muted-foreground">
                  Bills from configured vendors are automatically parsed via API (no manual review needed!)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Settings (Future) */}
      <Card>
        <CardHeader>
          <CardTitle>Other Integrations</CardTitle>
          <CardDescription>
            Additional integrations will be available here
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <p>More integrations coming soon:</p>
            <ul className="text-sm mt-2 space-y-1">
              <li>Yardi API</li>
              <li>QuickBooks OAuth</li>
              <li>Xero OAuth</li>
              <li>Custom webhook endpoints</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
